import type { NoticeEntry, Snapshot } from "../services/generated/client.js";
import type { SideLabel } from "../services/generated/client.js";

/**
 * Turning a notice into a sentence — phase 16d.
 *
 * Pure, and shared by both renderers: the phone draws a toast **and** a feed,
 * the television draws the toast only. Getting the stream onto the wire was the
 * expensive half; a second renderer over the same sentences is nearly free,
 * which is the whole reason the decision was "both" rather than "one".
 *
 * # Naming the stolen card, without duplicating it on the wire
 *
 * `Notice::Stole` carries a track id and no card. By the time anybody renders
 * it the card is on the thief's timeline, where its title and year are already
 * public — so this resolves it from the snapshot rather than the server sending
 * a second copy that can drift. If it cannot be resolved (a blind timeline, a
 * card stolen again since) the generic sentence is used, which is still the one
 * the victim needed: *somebody took a card from you*.
 */

/** How to address a side: their name, or "You". */
export function sideName(
  roster: readonly SideLabel[],
  sideId: string,
  yourSide: string | null,
  you: string,
): string {
  if (sideId === yourSide) {
    return you;
  }
  return roster.find((entry) => entry.side === sideId)?.name ?? sideId.slice(0, 4);
}

/** The title of a card, if the board still shows it. */
export function trackTitle(board: Snapshot, track: string): string | null {
  for (const side of board.sides) {
    const card = side.timeline.find((held) => held.track === track);
    if (card) {
      return card.title;
    }
  }
  return null;
}

/**
 * Entries a client has not rendered yet.
 *
 * Keyed on `seq` rather than on a diff of two snapshots, which is what makes it
 * survive a reconnect: a phone that missed three publications still knows the
 * highest sequence it has shown.
 */
export function unseen(notices: readonly NoticeEntry[], lastSeen: number): NoticeEntry[] {
  return notices.filter((entry) => entry.seq > lastSeen);
}

/** The highest sequence in a list, or the current mark if it is empty. */
export function highestSeq(notices: readonly NoticeEntry[], fallback: number): number {
  return notices.reduce((top, entry) => Math.max(top, entry.seq), fallback);
}
